/**
 * Onboarding gym step (web) — State → City → Gym progressive search.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { createLogger } from '@/lib/logger';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  TextInput,
  ScrollView,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { ChevronLeft, CheckCircle, X, Home, ChevronDown } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';

const log = createLogger('ONBOARD_GYM_WEB');

const US_STATES = [
  {abbr:'AL',name:'Alabama'},{abbr:'AK',name:'Alaska'},{abbr:'AZ',name:'Arizona'},
  {abbr:'AR',name:'Arkansas'},{abbr:'CA',name:'California'},{abbr:'CO',name:'Colorado'},
  {abbr:'CT',name:'Connecticut'},{abbr:'DC',name:'District of Columbia'},
  {abbr:'DE',name:'Delaware'},{abbr:'FL',name:'Florida'},{abbr:'GA',name:'Georgia'},
  {abbr:'HI',name:'Hawaii'},{abbr:'ID',name:'Idaho'},{abbr:'IL',name:'Illinois'},
  {abbr:'IN',name:'Indiana'},{abbr:'IA',name:'Iowa'},{abbr:'KS',name:'Kansas'},
  {abbr:'KY',name:'Kentucky'},{abbr:'LA',name:'Louisiana'},{abbr:'ME',name:'Maine'},
  {abbr:'MD',name:'Maryland'},{abbr:'MA',name:'Massachusetts'},{abbr:'MI',name:'Michigan'},
  {abbr:'MN',name:'Minnesota'},{abbr:'MS',name:'Mississippi'},{abbr:'MO',name:'Missouri'},
  {abbr:'MT',name:'Montana'},{abbr:'NE',name:'Nebraska'},{abbr:'NV',name:'Nevada'},
  {abbr:'NH',name:'New Hampshire'},{abbr:'NJ',name:'New Jersey'},{abbr:'NM',name:'New Mexico'},
  {abbr:'NY',name:'New York'},{abbr:'NC',name:'North Carolina'},{abbr:'ND',name:'North Dakota'},
  {abbr:'OH',name:'Ohio'},{abbr:'OK',name:'Oklahoma'},{abbr:'OR',name:'Oregon'},
  {abbr:'PA',name:'Pennsylvania'},{abbr:'RI',name:'Rhode Island'},{abbr:'SC',name:'South Carolina'},
  {abbr:'SD',name:'South Dakota'},{abbr:'TN',name:'Tennessee'},{abbr:'TX',name:'Texas'},
  {abbr:'UT',name:'Utah'},{abbr:'VT',name:'Vermont'},{abbr:'VA',name:'Virginia'},
  {abbr:'WA',name:'Washington'},{abbr:'WV',name:'West Virginia'},{abbr:'WI',name:'Wisconsin'},
  {abbr:'WY',name:'Wyoming'},
];

type GymRow = { id: string; name: string; address: string; city: string; state: string; gym_code: string };

export default function OnboardingHomeGymWeb() {
  const router = useRouter();
  const { user, refreshProfile } = useAuth();
  const { theme } = useTheme();
  const c = theme.colors;

  // Gym code tab state
  const [gymCode, setGymCode] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState('');

  // Common state
  const [selectedGym, setSelectedGym] = useState<{ id: string; name: string; address?: string; city?: string; state?: string; isHomeGym?: boolean } | null>(null);
  const [searchMode, setSearchMode] = useState<'search' | 'code'>('search');
  const [saving, setSaving] = useState(false);

  // Progressive search state
  const [selectedState, setSelectedState] = useState<string | null>(null);
  const [stateDropdownOpen, setStateDropdownOpen] = useState(false);
  const [cityQuery, setCityQuery] = useState('');
  const [cityResults, setCityResults] = useState<string[]>([]);
  const [cityLoading, setCityLoading] = useState(false);
  const [selectedCity, setSelectedCity] = useState<string | null>(null);
  const [gymQuery, setGymQuery] = useState('');
  const [gymResults, setGymResults] = useState<GymRow[]>([]);
  const [gymLoading, setGymLoading] = useState(false);

  // City autocomplete — debounced
  useEffect(() => {
    if (!selectedState || cityQuery.trim().length < 1) { setCityResults([]); return; }
    const t = setTimeout(async () => {
      setCityLoading(true);
      try {
        const { data } = await supabase
          .from('gyms')
          .select('city')
          .ilike('state', selectedState)
          .ilike('city', `%${cityQuery.trim()}%`)
          .order('city')
          .limit(100);
        const unique = [...new Set((data || []).map(r => r.city).filter(Boolean))].slice(0, 20);
        setCityResults(unique);
      } catch { setCityResults([]); }
      setCityLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [selectedState, cityQuery]);

  // Gym search — debounced
  useEffect(() => {
    if (!selectedState) { setGymResults([]); return; }
    // need at least a city or gym query
    if (!selectedCity && gymQuery.trim().length < 2) { setGymResults([]); return; }
    const t = setTimeout(async () => {
      setGymLoading(true);
      try {
        let query = supabase
          .from('gyms')
          .select('id, name, address, city, state, gym_code')
          .ilike('state', selectedState);
        if (selectedCity) query = query.ilike('city', selectedCity);
        if (gymQuery.trim().length >= 2) query = query.ilike('name', `%${gymQuery.trim()}%`);
        const { data } = await query.order('name').limit(30);
        setGymResults(data || []);
      } catch { setGymResults([]); }
      setGymLoading(false);
    }, 300);
    return () => clearTimeout(t);
  }, [selectedState, selectedCity, gymQuery]);

  const lookupGymCode = useCallback(async () => {
    const code = gymCode.trim().toUpperCase();
    if (!code) return;
    setLookupLoading(true);
    setLookupError('');
    try {
      const { data, error } = await supabase
        .from('gyms')
        .select('id, name, address, city, state')
        .ilike('gym_code', code)
        .limit(1)
        .maybeSingle();
      if (error) {
        setLookupError('Something went wrong. Try again.');
      } else if (!data) {
        const { data: orgData } = await supabase
          .from('organizations')
          .select('id, name')
          .ilike('gym_code', code)
          .limit(1)
          .maybeSingle();
        if (orgData) {
          setSelectedGym({ id: orgData.id, name: orgData.name });
        } else {
          setLookupError('No gym found with that code. Check with your gym.');
        }
      } else {
        setSelectedGym({ id: data.id, name: data.name, address: data.address, city: data.city, state: data.state });
      }
    } catch {
      setLookupError('Something went wrong. Try again.');
    }
    setLookupLoading(false);
  }, [gymCode]);

  const saveGymAndContinue = async () => {
    setSaving(true);
    try {
      if (user?.id) {
        const isHomeGym = selectedGym?.isHomeGym === true;
        let gymTableId: string | null = null;

        if (selectedGym && isHomeGym) {
          const { data: newGym, error: gymError } = await supabase
            .from('gyms')
            .insert({ name: 'My Home Gym', address: 'Home Gym', lat: 0, lng: 0, is_home_gym: true, created_by: user.id })
            .select('id')
            .single();
          if (gymError) log.warn('Failed to create home gym:', gymError);
          if (newGym) gymTableId = newGym.id;
        } else if (selectedGym && selectedGym.id !== 'pending') {
          gymTableId = selectedGym.id;
        }

        if (gymTableId) {
          await supabase.from('profiles').update({ gym_id: gymTableId, home_gym_id: gymTableId }).eq('id', user.id);
        }
        if (gymTableId) {
          try {
            await supabase.from('gym_memberships').upsert(
              { gym_id: gymTableId, user_id: user.id, role: 'member', status: 'active' },
              { onConflict: 'gym_id,user_id' },
            );
          } catch {}
        }
        await refreshProfile?.();
      }
    } catch (err) {
      log.warn('Failed to save gym selection', err);
    }
    setSaving(false);
    router.replace('/(onboarding)/handle');
  };

  const clearAll = () => {
    setSelectedGym(null);
    setGymCode('');
    setLookupError('');
    setSelectedState(null);
    setSelectedCity(null);
    setCityQuery('');
    setGymQuery('');
    setCityResults([]);
    setGymResults([]);
  };

  return (
    <View style={[styles.container, { backgroundColor: c.background }]}>
      <TouchableOpacity style={styles.backBtn} onPress={() => router.back()}>
        <ChevronLeft size={24} color={c.text} />
      </TouchableOpacity>

      <ScrollView contentContainerStyle={styles.scrollContent} keyboardShouldPersistTaps="handled">
        <View style={styles.inner}>
          <Text style={[styles.title, { color: c.text, fontFamily: TYPO.heading.bold }]}>Join Your Gym</Text>
          <Text style={[styles.subtitle, { color: c.textSecondary }]}>
            Find your gym by state and city, or enter a gym code.
          </Text>

          {/* Home Gym Option */}
          {!selectedGym && (
            <TouchableOpacity
              style={{ backgroundColor: c.surface, borderRadius: 2, padding: 14, width: '100%', borderWidth: 1, borderColor: c.border, flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 8 }}
              onPress={() => setSelectedGym({ id: 'home_gym', name: 'My Home Gym', isHomeGym: true })}
            >
              <Home size={22} color={c.accent} />
              <View style={{ flex: 1 }}>
                <Text style={{ fontSize: 15, fontWeight: '700', color: c.text }}>I Lift at Home</Text>
                <Text style={{ fontSize: 12, color: c.textSecondary }}>Create a personal home gym</Text>
              </View>
            </TouchableOpacity>
          )}

          {selectedGym ? (
            <View style={[styles.gymSelected, { backgroundColor: c.surface, borderColor: c.accent }]}>
              <View style={styles.gymSelectedRow}>
                <CheckCircle size={20} color={c.accent} />
                <View style={{ flex: 1 }}>
                  <Text style={[styles.gymSelectedText, { color: c.text, fontFamily: TYPO.body.semibold }]}>{selectedGym.name}</Text>
                  {selectedGym.address && !selectedGym.isHomeGym && (
                    <Text style={{ color: c.textSecondary, fontSize: 12, marginTop: 2 }}>{selectedGym.address}</Text>
                  )}
                  {selectedGym.city && selectedGym.state && (
                    <Text style={{ color: c.textTertiary, fontSize: 11, marginTop: 1 }}>{selectedGym.city}, {selectedGym.state}</Text>
                  )}
                </View>
              </View>
              <TouchableOpacity onPress={clearAll}>
                <X size={18} color={c.textTertiary} />
              </TouchableOpacity>
            </View>
          ) : (
            <View style={{ width: '100%', gap: 12 }}>
              {/* Tab toggle */}
              <View style={{ flexDirection: 'row', borderRadius: 8, overflow: 'hidden', borderWidth: 1, borderColor: c.border }}>
                <TouchableOpacity
                  style={{ flex: 1, paddingVertical: 10, alignItems: 'center', backgroundColor: searchMode === 'search' ? c.accent : c.surface }}
                  onPress={() => { setSearchMode('search'); setLookupError(''); }}
                >
                  <Text style={{ color: searchMode === 'search' ? '#000' : c.textSecondary, fontFamily: TYPO.heading.bold, fontSize: 13, letterSpacing: 1 }}>GYM SEARCH</Text>
                </TouchableOpacity>
                <TouchableOpacity
                  style={{ flex: 1, paddingVertical: 10, alignItems: 'center', backgroundColor: searchMode === 'code' ? c.accent : c.surface }}
                  onPress={() => { setSearchMode('code'); }}
                >
                  <Text style={{ color: searchMode === 'code' ? '#000' : c.textSecondary, fontFamily: TYPO.heading.bold, fontSize: 13, letterSpacing: 1 }}>GYM CODE</Text>
                </TouchableOpacity>
              </View>

              {searchMode === 'search' ? (
                <View style={{ gap: 12, width: '100%' }}>
                  {/* Step 1: State selector */}
                  <View>
                    <Text style={{ color: c.textSecondary, fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>State</Text>
                    {selectedState ? (
                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                        <View style={{ backgroundColor: c.accent, borderRadius: 4, paddingHorizontal: 10, paddingVertical: 6, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                          <Text style={{ color: '#000', fontWeight: '700', fontSize: 14 }}>{selectedState}</Text>
                          <TouchableOpacity onPress={() => { setSelectedState(null); setSelectedCity(null); setCityQuery(''); setGymQuery(''); setCityResults([]); setGymResults([]); }} hitSlop={8}>
                            <X size={14} color="#000" />
                          </TouchableOpacity>
                        </View>
                        <Text style={{ color: c.textTertiary, fontSize: 13 }}>{US_STATES.find(s => s.abbr === selectedState)?.name}</Text>
                      </View>
                    ) : (
                      <View>
                        <TouchableOpacity
                          style={[styles.codeInputRow, { backgroundColor: c.surface, borderColor: c.border, justifyContent: 'space-between' }]}
                          onPress={() => setStateDropdownOpen(!stateDropdownOpen)}
                        >
                          <Text style={{ color: c.textTertiary, fontSize: 14, paddingLeft: 14, paddingVertical: 14 }}>Select a state…</Text>
                          <ChevronDown size={18} color={c.textTertiary} style={{ marginRight: 12 }} />
                        </TouchableOpacity>
                        {stateDropdownOpen && (
                          <ScrollView style={{ maxHeight: 280, borderWidth: 1, borderColor: c.border, borderRadius: 8, backgroundColor: c.surface, marginTop: 4 }} nestedScrollEnabled>
                            {US_STATES.map(s => (
                              <TouchableOpacity
                                key={s.abbr}
                                style={{ paddingVertical: 10, paddingHorizontal: 14, borderBottomWidth: 1, borderBottomColor: c.border }}
                                onPress={() => { setSelectedState(s.abbr); setStateDropdownOpen(false); setSelectedCity(null); setCityQuery(''); setGymQuery(''); setCityResults([]); setGymResults([]); }}
                              >
                                <Text style={{ color: c.text, fontSize: 14 }}><Text style={{ fontWeight: '700' }}>{s.abbr}</Text>  {s.name}</Text>
                              </TouchableOpacity>
                            ))}
                          </ScrollView>
                        )}
                      </View>
                    )}
                  </View>

                  {/* Step 2: City autocomplete */}
                  {selectedState && (
                    <View>
                      <Text style={{ color: c.textSecondary, fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>City</Text>
                      {selectedCity ? (
                        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                          <View style={{ backgroundColor: c.accent, borderRadius: 4, paddingHorizontal: 10, paddingVertical: 6, flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                            <Text style={{ color: '#000', fontWeight: '700', fontSize: 14 }}>{selectedCity}</Text>
                            <TouchableOpacity onPress={() => { setSelectedCity(null); setCityQuery(''); setGymQuery(''); setGymResults([]); }} hitSlop={8}>
                              <X size={14} color="#000" />
                            </TouchableOpacity>
                          </View>
                        </View>
                      ) : (
                        <View>
                          <View style={[styles.codeInputRow, { backgroundColor: c.surface, borderColor: c.border }]}>  
                            <TextInput
                              style={[styles.codeInput, { color: c.text, letterSpacing: 0, fontWeight: '500', fontSize: 14 }]}
                              placeholder="Start typing a city…"
                              placeholderTextColor={c.textTertiary}
                              value={cityQuery}
                              onChangeText={setCityQuery}
                              autoCorrect={false}
                              returnKeyType="next"
                            />
                            {cityLoading && <ActivityIndicator size="small" color={c.accent} style={{ marginRight: 12 }} />}
                          </View>
                          {cityResults.length > 0 && (
                            <ScrollView style={{ maxHeight: 180, borderWidth: 1, borderColor: c.border, borderRadius: 8, backgroundColor: c.surface, marginTop: 4 }} nestedScrollEnabled>
                              {cityResults.map(city => (
                                <TouchableOpacity
                                  key={city}
                                  style={{ paddingVertical: 10, paddingHorizontal: 14, borderBottomWidth: 1, borderBottomColor: c.border }}
                                  onPress={() => { setSelectedCity(city); setCityQuery(''); setCityResults([]); }}
                                >
                                  <Text style={{ color: c.text, fontSize: 14 }}>{city}</Text>
                                </TouchableOpacity>
                              ))}
                            </ScrollView>
                          )}
                          {cityQuery.length >= 2 && !cityLoading && cityResults.length === 0 && (
                            <Text style={{ color: c.textTertiary, fontSize: 12, marginTop: 4 }}>No cities found for {selectedState}.</Text>
                          )}
                        </View>
                      )}
                    </View>
                  )}

                  {/* Step 3: Gym name search */}
                  {selectedState && (selectedCity || true) && (
                    <View>
                      <Text style={{ color: c.textSecondary, fontSize: 11, fontWeight: '700', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Gym Name</Text>
                      <View style={[styles.codeInputRow, { backgroundColor: c.surface, borderColor: c.border }]}>  
                        <TextInput
                          style={[styles.codeInput, { color: c.text, letterSpacing: 0, fontWeight: '500', fontSize: 16 }]}
                          placeholder={selectedCity ? `Search gyms in ${selectedCity}…` : `Search gyms in ${selectedState}…`}
                          placeholderTextColor={c.textTertiary}
                          value={gymQuery}
                          onChangeText={setGymQuery}
                          autoCorrect={false}
                          returnKeyType="search"
                        />
                        {gymLoading && <ActivityIndicator size="small" color={c.accent} style={{ marginRight: 12 }} />}
                      </View>
                      {gymResults.length > 0 && (
                        <ScrollView style={{ maxHeight: 260, borderWidth: 1, borderColor: c.border, borderRadius: 8, backgroundColor: c.surface, marginTop: 4 }} nestedScrollEnabled>
                          {gymResults.map(gym => (
                            <TouchableOpacity
                              key={gym.id}
                              style={{ paddingVertical: 12, paddingHorizontal: 14, borderBottomWidth: 1, borderBottomColor: c.border }}
                              onPress={() => {
                                setSelectedGym({ id: gym.id, name: gym.name, address: gym.address, city: gym.city, state: gym.state });
                                setGymResults([]);
                                setGymQuery('');
                              }}
                            >
                              <Text style={{ color: c.text, fontSize: 15, fontFamily: TYPO.body.semibold }}>{gym.name}</Text>
                              {gym.address ? <Text style={{ color: c.textSecondary, fontSize: 12, marginTop: 2 }}>{gym.address}</Text> : null}
                              <Text style={{ color: c.textTertiary, fontSize: 11, marginTop: 1 }}>{gym.city}, {gym.state}</Text>
                            </TouchableOpacity>
                          ))}
                        </ScrollView>
                      )}
                      {(selectedCity || gymQuery.length >= 2) && !gymLoading && gymResults.length === 0 && selectedState && (
                        <Text style={{ color: c.textTertiary, fontSize: 12, marginTop: 4 }}>No gyms found. Try a different search or use a gym code.</Text>
                      )}
                    </View>
                  )}
                </View>
              ) : (
                <>
                  <View style={[styles.codeInputRow, { backgroundColor: c.surface, borderColor: c.border }]}>
                    <TextInput
                      style={[styles.codeInput, { color: c.text }]}
                      placeholder="e.g. PLN-FRISCO-TX-001"
                      placeholderTextColor={c.textTertiary}
                      value={gymCode}
                      onChangeText={(t) => { setGymCode(t.toUpperCase()); setLookupError(''); }}
                      autoCapitalize="characters"
                      autoCorrect={false}
                      onSubmitEditing={lookupGymCode}
                      returnKeyType="go"
                    />
                    <TouchableOpacity
                      style={[styles.joinBtn, { backgroundColor: gymCode.trim() ? c.accent : c.surface }]}
                      onPress={lookupGymCode}
                      disabled={!gymCode.trim() || lookupLoading}
                    >
                      {lookupLoading ? (
                        <ActivityIndicator size="small" color="#000" />
                      ) : (
                        <Text style={{ color: gymCode.trim() ? '#000' : c.textTertiary, fontFamily: TYPO.heading.bold, fontSize: 14, letterSpacing: 1 }}>JOIN</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                  {lookupError ? <Text style={{ color: '#ef4444', fontSize: 13 }}>{lookupError}</Text> : null}
                  <Text style={{ color: c.textTertiary, fontSize: 12 }}>Ask your gym for their DUEL code, or search by name instead.</Text>
                </>
              )}
            </View>
          )}
        </View>

        <View style={styles.bottom}>
          <TouchableOpacity
            style={[styles.primaryBtn, { backgroundColor: selectedGym ? c.accent : c.surface, borderWidth: selectedGym ? 0 : 1, borderColor: c.border, opacity: saving ? 0.6 : 1 }]}
            onPress={saveGymAndContinue}
            disabled={saving || !selectedGym}
          >
            <Text style={[styles.primaryBtnText, { color: selectedGym ? '#000' : c.textTertiary, fontFamily: TYPO.heading.bold }]}>
              {saving ? 'Setting up…' : 'CONTINUE'}
            </Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  backBtn: { position: 'absolute', top: 50, left: 16, zIndex: 10, padding: 8 },
  scrollContent: { flexGrow: 1 },
  inner: { flex: 1, paddingHorizontal: 24, paddingTop: 80, alignItems: 'center', gap: 16 },
  title: { fontSize: 28, textAlign: 'center', textTransform: 'uppercase', letterSpacing: 1 },
  subtitle: { fontSize: 15, textAlign: 'center', lineHeight: 22, maxWidth: 340, marginBottom: 8 },
  codeInputRow: { flexDirection: 'row', alignItems: 'center', borderWidth: 1, borderRadius: 8, padding: 4, gap: 8 },
  codeInput: { flex: 1, fontSize: 18, paddingVertical: 14, paddingLeft: 14, paddingRight: 4, letterSpacing: 2, fontWeight: '700' },
  joinBtn: { paddingHorizontal: 16, paddingVertical: 10, alignItems: 'center', justifyContent: 'center', minWidth: 60, flexShrink: 0, borderRadius: 4 },
  gymSelected: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', borderWidth: 2, borderRadius: 2, paddingVertical: 14, paddingHorizontal: 16, width: '100%' },
  gymSelectedRow: { flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1 },
  gymSelectedText: { fontSize: 16 },
  bottom: { paddingHorizontal: 24, paddingBottom: 40, gap: 4, alignItems: 'center' },
  primaryBtn: { width: '100%', paddingVertical: 18, borderRadius: 0, alignItems: 'center' },
  primaryBtnText: { fontSize: 18, textTransform: 'uppercase', letterSpacing: 1 },
});
